/**
 * Project Files Service
 * Servicio moderno para subida y gestión de archivos de proyectos
 * Reemplaza el sistema antiguo de AGTHallService
 */

import axios from 'axios';

// Configurar base URL - asegurar que siempre termine en /api
const getBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
  // Si ya tiene /api, usarlo como está, si no, agregarlo
  return envUrl.endsWith('/api') ? envUrl : `${envUrl}/api`;
};

const API_BASE_URL = getBaseUrl();

/**
 * Sube archivos al proyecto usando el sistema avanzado de procesamiento
 * @param {string} userId - ID del usuario
 * @param {string} projectId - ID del proyecto
 * @param {File[]} files - Array de archivos a subir
 * @returns {Promise<Object>} Resultado de la subida
 */
export const uploadProjectFiles = async (userId, projectId, files) => {
  try {
    const formData = new FormData();

    // Agregar userId al formData
    formData.append('userId', userId);

    // Agregar cada archivo al formData
    files.forEach((file) => {
      formData.append('files', file);
    });

    const response = await axios.post(
      `${API_BASE_URL}/projects/${projectId}/files`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        // Timeout más largo para archivos grandes
        timeout: 120000, // 2 minutos
      }
    );

    return response.data;
  } catch (error) {
    console.error('[ProjectFiles] Upload error:', error);
    throw new Error(
      error.response?.data?.error || error.message || 'Error uploading files'
    );
  }
};

/**
 * Obtiene la lista de archivos de un proyecto
 * @param {string} userId - ID del usuario
 * @param {string} projectId - ID del proyecto
 * @returns {Promise<Array>} Lista de archivos
 */
export const getProjectFiles = async (userId, projectId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/projects/${projectId}/files`,
      {
        params: { userId },
      }
    );

    return response.data.files || [];
  } catch (error) {
    console.error('[ProjectFiles] Get files error:', error);
    throw new Error(
      error.response?.data?.error || error.message || 'Error fetching files'
    );
  }
};

/**
 * Elimina un archivo del proyecto
 * @param {string} userId - ID del usuario
 * @param {string} projectId - ID del proyecto
 * @param {string} fileName - Nombre del archivo a eliminar
 * @returns {Promise<Object>} Resultado de la eliminación
 */
export const deleteProjectFile = async (userId, projectId, fileName) => {
  try {
    const response = await axios.delete(
      `${API_BASE_URL}/projects/${projectId}/files/${encodeURIComponent(fileName)}`,
      {
        data: { userId },
      }
    );

    return response.data;
  } catch (error) {
    console.error('[ProjectFiles] Delete file error:', error);
    throw new Error(
      error.response?.data?.error || error.message || 'Error deleting file'
    );
  }
};

/**
 * Obtiene información detallada de un archivo
 * @param {string} userId - ID del usuario
 * @param {string} projectId - ID del proyecto
 * @param {string} fileName - Nombre del archivo
 * @returns {Promise<Object>} Información del archivo
 */
export const getFileInfo = async (userId, projectId, fileName) => {
  try {
    const files = await getProjectFiles(userId, projectId);
    return files.find(f => f.fileName === fileName) || null;
  } catch (error) {
    console.error('[ProjectFiles] Get file info error:', error);
    throw new Error(
      error.message || 'Error fetching file info'
    );
  }
};

export default {
  uploadProjectFiles,
  getProjectFiles,
  deleteProjectFile,
  getFileInfo,
};
